import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataSource } from '../../config/capabilities';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { MetadataList } from '../../shared/detail/MetadataList';
import type { MockTransactionReversalApproval, TransactionGapRepository } from './mockRepository';
import type { TransactionDetail } from './types';

export function TransactionReversalDialog({
  dataSource,
  detail,
  onClose,
  open,
  repository,
}: {
  readonly dataSource: DataSource;
  readonly detail: TransactionDetail;
  readonly onClose: () => void;
  readonly open: boolean;
  readonly repository: TransactionGapRepository | null;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [approval, setApproval] = useState<MockTransactionReversalApproval | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const eligible = isReversalEligible(detail);
  const signedImpact = calculateReversalImpact(detail);

  const close = () => {
    if (busy) return;
    setReason('');
    setConfirmed(false);
    setApproval(null);
    setFailed(false);
    submitting.current = false;
    onClose();
  };

  const submit = async () => {
    if (
      dataSource !== 'mock' ||
      repository === null ||
      !eligible ||
      reason.trim().length === 0 ||
      !confirmed ||
      submitting.current
    )
      return;
    submitting.current = true;
    setBusy(true);
    setFailed(false);
    try {
      setApproval(
        await repository.submitReversal({
          transactionId: detail.id,
          persistedStatus: detail.persistedStatus,
          reversalState: detail.reversalState,
          reason: reason.trim(),
          signedImpact,
          currencyCode: detail.currencyCode,
        }),
      );
    } catch {
      submitting.current = false;
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      aria-describedby="transaction-reversal-description"
      fullWidth
      maxWidth="sm"
      onClose={close}
      open={open}
    >
      <DialogTitle>{t('transactions.reversal.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <DataSourceBadge source={dataSource} />
          <DialogContentText id="transaction-reversal-description">
            {t('transactions.reversal.description')}
          </DialogContentText>
          {dataSource === 'unavailable' ? (
            <Alert severity="warning">{t('capability.backendUnavailable')}</Alert>
          ) : approval !== null ? (
            <Alert severity="success">
              {t('transactions.reversal.approvalCreated', { approvalId: approval.approvalId })}
            </Alert>
          ) : (
            <>
              <Paper component="section" sx={{ p: 2 }} variant="outlined">
                <Typography component="h3" sx={{ mb: 1 }} variant="h6">
                  {t('transactions.reversal.impact')}
                </Typography>
                <MetadataList
                  items={[
                    {
                      label: t('transactions.fields.transactionId'),
                      value: detail.id,
                    },
                    {
                      label: t('transactions.reversal.transactionState'),
                      value: detail.persistedStatus,
                    },
                    {
                      label: t('transactions.fields.reversalState'),
                      value: detail.reversalState,
                    },
                    {
                      label: t('transactions.reversal.signedImpact'),
                      value: formatSignedMoney(signedImpact, detail.currencyCode),
                    },
                  ]}
                />
              </Paper>
              <Alert severity="info">{t('transactions.reversal.dualControl')}</Alert>
              {eligible ? null : (
                <Alert severity="warning">{t('transactions.reversal.ineligible')}</Alert>
              )}
              <TextField
                autoFocus
                disabled={!eligible}
                label={t('sensitiveAction.reason')}
                minRows={2}
                multiline
                onChange={(event) => setReason(event.target.value)}
                required
                slotProps={{ htmlInput: { maxLength: 2000 } }}
                value={reason}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={confirmed}
                    disabled={!eligible}
                    onChange={(event) => setConfirmed(event.target.checked)}
                  />
                }
                label={t('transactions.reversal.confirmImpact')}
              />
            </>
          )}
          {failed ? <Alert severity="error">{t('transactions.reversal.error')}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={close}>
          {t('actions.close')}
        </Button>
        {dataSource === 'mock' && approval === null ? (
          <Button
            disabled={!eligible || reason.trim().length === 0 || !confirmed || busy}
            onClick={() => void submit()}
            variant="contained"
          >
            {t('transactions.reversal.submit')}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}

export function isReversalEligible(detail: TransactionDetail): boolean {
  return detail.persistedStatus === 'CALCULATED' && detail.reversalState === 'NOT_REVERSED';
}

export function calculateReversalImpact(detail: TransactionDetail): string {
  const values = detail.commissionLedgerEntries
    .filter((entry) => entry.entryType === 'EARN')
    .map((entry) => entry.signedAmount);
  if (values.length === 0) return '0';
  const scale = Math.max(...values.map((value) => value.split('.')[1]?.length ?? 0));
  const sum = values.reduce((total, value) => total + decimalUnits(value, scale), 0n);
  return formatUnits(-sum, scale);
}

function decimalUnits(value: string, scale: number): bigint {
  const match = /^(-)?(0|[1-9][0-9]*)(?:\.([0-9]+))?$/u.exec(value);
  if (match === null) throw new Error('Expected an exact signed decimal');
  const magnitude =
    BigInt(match[2] ?? '0') * 10n ** BigInt(scale) +
    BigInt((match[3] ?? '').padEnd(scale, '0') || '0');
  return match[1] === '-' ? -magnitude : magnitude;
}

function formatUnits(value: bigint, scale: number): string {
  const negative = value < 0n;
  const digits = (negative ? -value : value).toString().padStart(scale + 1, '0');
  const amount = scale === 0 ? digits : `${digits.slice(0, -scale)}.${digits.slice(-scale)}`;
  return `${negative ? '-' : ''}${amount}`;
}

function formatSignedMoney(amount: string, currencyCode: string): string {
  const negative = amount.startsWith('-');
  return `${negative ? '-' : ''}${currencyCode}\u00a0${negative ? amount.slice(1) : amount}`;
}
